import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, Loader2, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { DashboardAPI } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

const REFRESH_INTERVAL_MS = 60_000;

function rupees(n) {
    if (n == null) return "₹0";
    return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

export function DviUpsellTile({ className }) {
    const { permissions } = useAuth();
    const canSee = (permissions || []).includes("dvi.inspection.read");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [drillOpen, setDrillOpen] = useState(false);
    const [drillItems, setDrillItems] = useState(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!canSee) return;
        let alive = true;
        DashboardAPI.dviUpsellToday().then((d) => {
            if (alive) setData(d);
        }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [canSee, tick]);

    // 60s polling while tile is mounted.
    useEffect(() => {
        if (!canSee) return;
        const id = setInterval(() => setTick((t) => t + 1), REFRESH_INTERVAL_MS);
        return () => clearInterval(id);
    }, [canSee]);

    async function openDrill() {
        setDrillOpen(true);
        if (!drillItems) {
            try {
                const r = await DashboardAPI.dviUpsellTodayFindings();
                setDrillItems(r.items || []);
            } catch (e) {
                setDrillItems([]);
            }
        }
    }

    if (!canSee) return null;

    const total = data?.approved_upsell_inr_today ?? 0;
    const count = data?.approved_findings_count_today ?? 0;

    return (
        <>
            <Card
                onClick={openDrill}
                className={cn(
                    "group relative cursor-pointer overflow-hidden border-l-4 border-emerald-500 p-4 transition-all hover:shadow-md",
                    className,
                )}
                data-testid="dvi-upsell-tile"
            >
                <div className="flex items-start justify-between">
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                            <ClipboardCheck className="h-3 w-3" />
                            Customer-approved upsell · Today
                        </div>
                        <div className="mt-1 flex items-baseline gap-2">
                            {loading ? (
                                <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
                            ) : (
                                <>
                                    <span
                                        className="font-display text-3xl font-bold tracking-tight"
                                        data-testid="dvi-upsell-tile-total"
                                    >
                                        {rupees(total)}
                                    </span>
                                    <span className="text-sm text-zinc-500">
                                        · {count} item{count === 1 ? "" : "s"}
                                    </span>
                                </>
                            )}
                        </div>
                        <p className="mt-1 text-xs text-zinc-500">
                            From DVI customer approvals today. Click for breakdown.
                        </p>
                    </div>
                    <TrendingUp className="h-5 w-5 text-emerald-500 transition-transform group-hover:scale-110" />
                </div>
            </Card>

            <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
                <DialogContent className="max-w-lg" data-testid="dvi-upsell-drill">
                    <DialogHeader>
                        <DialogTitle>Today's approved DVI upsell</DialogTitle>
                        <DialogDescription>
                            {data?.window?.timezone ? `Window: tenant local day (${data.window.timezone})` : ""}
                        </DialogDescription>
                    </DialogHeader>
                    {drillItems === null ? (
                        <div className="flex items-center justify-center py-6">
                            <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
                        </div>
                    ) : drillItems.length === 0 ? (
                        <div className="py-6 text-center text-sm text-zinc-500">
                            No approvals yet today.
                        </div>
                    ) : (
                        <div className="max-h-80 space-y-1 overflow-y-auto">
                            {drillItems.map((it) => (
                                <Link
                                    key={it.estimate_line_id}
                                    to={`/jobcards/${it.jobcard_id}/inspection`}
                                    onClick={() => setDrillOpen(false)}
                                    className="flex items-center justify-between rounded-md border border-zinc-200 px-3 py-2 transition-colors hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-800/60"
                                    data-testid={`dvi-upsell-row-${it.estimate_line_id}`}
                                >
                                    <div className="min-w-0">
                                        <div className="truncate text-sm font-medium">
                                            {it.item_label || it.description}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                            {it.severity ? `${it.severity} · ` : ""}
                                            job …{(it.jobcard_id || "").slice(-6)}
                                        </div>
                                    </div>
                                    <div className="ml-3 font-display text-base font-bold">
                                        {rupees(it.line_total_inr)}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
